import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "card-page" }
const _hoisted_2 = { class: "content-wrapper" }
const _hoisted_3 = { class: "sidebar" }
const _hoisted_4 = { class: "nav-card" }
const _hoisted_5 = { class: "nav-logo" }
const _hoisted_6 = { class: "logo-icon" }
const _hoisted_7 = ["src"]
const _hoisted_8 = { class: "nav-list" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = {
  class: "nav-menu-visual",
  "aria-hidden": "true"
}
const _hoisted_11 = ["src"]
const _hoisted_12 = { class: "main-content" }
const _hoisted_13 = { class: "main-title" }
const _hoisted_14 = { class: "cards-grid" }
const _hoisted_15 = ["onClick"]
const _hoisted_16 = ["src"]
const _hoisted_17 = { class: "dock-bar" }
const _hoisted_18 = { class: "fullscreen-card" }
const _hoisted_19 = { class: "fullscreen-content" }
const _hoisted_20 = { class: "fs-icon-wrap" }
const _hoisted_21 = ["src"]
const _hoisted_22 = {
  key: 1,
  class: "fs-emoji"
}
const _hoisted_23 = { class: "fs-hint" }
const _hoisted_24 = { class: "fs-progress" }

import logo2Image from '../assets/logo2.png'
import menuBgImage from '../assets/menu_bg.jpg'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getWordsList, type Word } from '@/api/course-word'
import { addHistory } from '@/api/history'
import { getAccessToken } from '@/config/axios/auth'

type WordCard = {
  id: number
  en: string
  zh: string
  icon: string
  bgColor: string
  coverUrl?: string
  audioUrl?: string
}

const PARENT_VERIFIED_STORAGE_KEY = 'tree-sprout-parent-verified'

export default /*@__PURE__*/_defineComponent({
  __name: 'word',
  setup(__props) {

const isGuestUser = computed(() => getAccessToken() == null)
const route = useRoute()
const router = useRouter()

const activeNav = ref('')
const showTranslation = ref(true)
const isAutoPlaying = ref(false)
const fullscreenCard = ref<WordCard | null>(null)
const currentPlayingIndex = ref(-1)
const isHeaderScrolled = ref(false)
const pausedIndex = ref(-1) // 保存暂停时的索引
const parentPasswordModalRef = ref(null)
const historyRecordedSeriesIds = new Set<number>()
let audioPlayer: HTMLAudioElement | null = null
let autoPlayTimer: ReturnType<typeof setInterval> | null = null

const wordList = ref<Word[]>([])

const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]


const letterBgColors = [
  'rgba(255, 140, 66, 0.15)',
  'rgba(91, 143, 249, 0.15)',
  'rgba(76, 175, 80, 0.15)',
  'rgba(255, 193, 7, 0.15)',
  'rgba(156, 39, 176, 0.15)',
  'rgba(0, 150, 136, 0.15)',
]

const getRouteSeriesId = () => {
  const rawSeriesId = Array.isArray(route.query.seriesId)
    ? route.query.seriesId[0]
    : route.query.seriesId
  const seriesId = Number(rawSeriesId)

  return Number.isFinite(seriesId) ? seriesId : undefined
}

const getRouteTitle = () => {
  const rawTitle = Array.isArray(route.query.title)
    ? route.query.title[0]
    : route.query.title
  const title = String(rawTitle ?? '').trim()

  if (!title) {
    return '26个英文字母'
  }

  try {
    return decodeURIComponent(title)
  } catch {
    return title
  }
}

const pageTitle = computed(() => getRouteTitle())

const getWordIcon = (english: unknown, index: number) => {
  const trimmedEnglish = String(english ?? '').trim()

  if (trimmedEnglish.length >= 2) {
    return trimmedEnglish.slice(0, 2)
  }

  if (trimmedEnglish.length === 1) {
    return `${trimmedEnglish}${trimmedEnglish.toLowerCase()}`
  }

  return String(index + 1)
}

const normalizeWordsResponse = (data: any): Word[] => {
  if (Array.isArray(data)) {
    return data
  }

  if (Array.isArray(data?.list)) {
    return data.list
  }

  return []
}

// 词卡数据
const recordWordHistory = (seriesId: number) => {
  if (!seriesId || historyRecordedSeriesIds.has(seriesId)) {
    return
  }
  if(!isGuestUser.value) {
    historyRecordedSeriesIds.add(seriesId)
    addHistory({
      contentType: 4,
      contentId: seriesId,
      seriesId
    }).catch(() => {
      historyRecordedSeriesIds.delete(seriesId)
    })
  }
}

const cards = computed<WordCard[]>(() => {
  return wordList.value.map((word, index) => {
    const english = word.english || ''

    return {
      id: word.id ?? index + 1,
      en: english,
      zh: word.chinese || '',
      icon: getWordIcon(english, index),
      bgColor: letterBgColors[index % letterBgColors.length],
      coverUrl: word.coverUrl,
      audioUrl: word.audioUrl,
    }
  })
})

// 过滤后的卡片
const filteredCards = computed(() => {
  return cards.value
})

// 当前卡片索引
const currentIndex = computed(() => {
  if (!fullscreenCard.value) return 0
  return filteredCards.value.findIndex(c => c.id === fullscreenCard.value.id)
})

// 关闭全屏
const closeFullscreen = () => {
  fullscreenCard.value = null
  stopAutoPlay()
}

// 上一张
const prevCard = () => {
  const idx = currentIndex.value
  const prev = (idx - 1 + filteredCards.value.length) % filteredCards.value.length
  fullscreenCard.value = filteredCards.value[prev]
  speakWord(fullscreenCard.value)
}

// 下一张
const nextCard = () => {
  const idx = currentIndex.value
  const next = (idx + 1) % filteredCards.value.length
  fullscreenCard.value = filteredCards.value[next]
  speakWord(fullscreenCard.value)
}

// 发音
const stopCurrentAudio = () => {
  if (!audioPlayer) {
    return
  }

  audioPlayer.pause()
  audioPlayer.currentTime = 0
  audioPlayer = null
}

const speakWord = (cardOrText: WordCard | string) => {
  const audioUrl = typeof cardOrText === 'string' ? '' : cardOrText.audioUrl
  const text = typeof cardOrText === 'string' ? cardOrText : cardOrText.en

  stopCurrentAudio()

  if (audioUrl) {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel()
    }

    const audio = new Audio(audioUrl)
    audioPlayer = audio
    audio.onended = () => {
      if (audioPlayer === audio) {
        audioPlayer = null
      }
      if (!isAutoPlaying.value) {
        currentPlayingIndex.value = -1
      }
    }
    audio.onerror = () => {
      if (audioPlayer === audio) {
        audioPlayer = null
      }
      playSpeechText(text)
    }
    audio.play().catch(() => {
      audioPlayer = null
      playSpeechText(text)
    })
    return
  }

  playSpeechText(text)
}

const playSpeechText = (text: string) => {
  if (!text || !('speechSynthesis' in window)) {
    return
  }

  speechSynthesis.cancel()
  const utterance = new SpeechSynthesisUtterance(text)
  utterance.lang = 'en-US'
  utterance.rate = 0.85
  utterance.onend = () => {
    if (!isAutoPlaying.value) {
      currentPlayingIndex.value = -1
    }
  }
  speechSynthesis.speak(utterance)
}

const getWordList = async () => {
  const seriesId = getRouteSeriesId()

  if (!seriesId) {
    wordList.value = []
    return
  }

  try {
    const data = await getWordsList(seriesId)
    wordList.value = normalizeWordsResponse(data)
    recordWordHistory(seriesId)
  } catch {
    wordList.value = []
  }
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/category')
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const handleNavItemClick = (item) => {
  if (item.id === 'home') {
    router.push('/')
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
  }
}

// 滚动到当前播放的卡片
const scrollToCard = (el) => {
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

// 切换翻译显示
const toggleTranslation = () => {
  showTranslation.value = !showTranslation.value
}



// 跳转到全屏页面
const goToFullscreen = () => {
  router.push({
    path: '/fullscreen',
    query: {
      seriesId: route.query.seriesId
    }
  })
}

// 自动播放
const toggleAutoPlay = () => {
  if (isAutoPlaying.value) {
    stopAutoPlay()
  } else {
    startAutoPlay()
  }
}

const startAutoPlay = () => {
  if (filteredCards.value.length > 0) {
    // 如果有暂停记录，从暂停位置继续；否则从第一个开始
    if (pausedIndex.value >= 0) {
      currentPlayingIndex.value = pausedIndex.value
    } else {
      currentPlayingIndex.value = 0
    }
    
    const currentCard = filteredCards.value[currentPlayingIndex.value]
    speakWord(currentCard)
    isAutoPlaying.value = true
    
    // 设置定时器，每3秒播放下一个
    autoPlayTimer = setInterval(() => {
      const nextIndex = (currentPlayingIndex.value + 1) % filteredCards.value.length
      currentPlayingIndex.value = nextIndex
      const nextCardItem = filteredCards.value[nextIndex]
      speakWord(nextCardItem)
    }, 3000)
  }
}

const stopAutoPlay = () => {
  isAutoPlaying.value = false
  // 保存当前播放位置
  pausedIndex.value = currentPlayingIndex.value
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer)
    autoPlayTimer = null
  }
  stopCurrentAudio()
  if ('speechSynthesis' in window) {
    speechSynthesis.cancel()
  }
}

// 占位功能
const openSettings = () => {
  alert('设置功能开发中...')
}

const openTestMode = () => {
  alert('测试模式开发中...')
}

// 键盘事件
const handleKeydown = (e) => {
  if (!fullscreenCard.value) return

  if (e.key === 'Escape') {
    closeFullscreen()
  } else if (e.key === 'ArrowLeft') {
    prevCard()
  } else if (e.key === 'ArrowRight') {
    nextCard()
  } else if (e.key === ' ') {
    e.preventDefault()
    speakWord(fullscreenCard.value)
  }
}

const handleCardClick = (card, index) => {
  currentPlayingIndex.value = index
  pausedIndex.value = index
  speakWord(card)
}

const handlePageScroll = () => {
  isHeaderScrolled.value = window.scrollY > 12
}

watch(
  () => route.query.seriesId,
  () => {
    stopAutoPlay()
    fullscreenCard.value = null
    currentPlayingIndex.value = -1
    pausedIndex.value = -1
    getWordList()
  }
)

onMounted(() => {
  getWordList()
  window.addEventListener('keydown', handleKeydown)
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('scroll', handlePageScroll)
  stopAutoPlay()
})

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[8] || (_cache[8] = _createElementVNode("div", { class: "bg-gradient" }, null, -1)),
    _cache[9] || (_cache[9] = _createElementVNode("div", { class: "orb orb-1" }, null, -1)),
    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "orb orb-2" }, null, -1)),
    _cache[11] || (_cache[11] = _createElementVNode("div", { class: "orb orb-3" }, null, -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("aside", _hoisted_3, [
        _createElementVNode("nav", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("img", {
                src: _unref(logo2Image),
                alt: "",
                class: "logo2-icon"
              }, null, 8, _hoisted_7)
            ]),
            _cache[1] || (_cache[1] = _createElementVNode("div", { class: "brand-copy" }, [
              _createElementVNode("h1", { class: "brand-name" }, "树苗英语"),
              _createElementVNode("span", { class: "brand-subtitle" }, "TREE SAPLING")
            ], -1))
          ]),
          _createElementVNode("ul", _hoisted_8, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(navItems, (item) => {
              return _createElementVNode("li", {
                key: item.id
              }, [
                _createElementVNode("a", {
                  href: "#",
                  onClick: _withModifiers(($event: any) => (handleNavItemClick(item)), ["prevent"]),
                  class: _normalizeClass(['nav-item', { active: activeNav.value === item.id }])
                }, [
                  _createVNode(_component_font_awesome_icon, {
                    icon: item.icon,
                    class: "nav-icon"
                  }, null, 8, ["icon"]),
                  _createElementVNode("span", null, _toDisplayString(item.label), 1)
                ], 10, _hoisted_9)
              ])
            }), 64))
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("img", {
              src: _unref(menuBgImage),
              alt: "",
              class: "nav-menu-image"
            }, null, 8, _hoisted_11)
          ])
        ])
      ]),
      _createElementVNode("main", _hoisted_12, [
        _createElementVNode("header", {
          class: _normalizeClass(["page-header", { 'is-scrolled': isHeaderScrolled.value }])
        }, [
          _createElementVNode("button", {
            type: "button",
            class: "mobile-back-btn",
            onClick: handleGoBack,
            "aria-label": "返回上一页"
          }, [
            _createVNode(_component_font_awesome_icon, { icon: "chevron-left" })
          ]),
          _createElementVNode("h1", _hoisted_13, _toDisplayString(pageTitle.value), 1)
        ], 2),
        _cache[2] || (_cache[2] = _createElementVNode("div", {
          class: "page-header-spacer",
          "aria-hidden": "true"
        }, null, -1)),
        _createElementVNode("div", _hoisted_14, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredCards.value, (card, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: card.id,
              class: _normalizeClass(["word-card", { playing: currentPlayingIndex.value === index }]),
              onClick: ($event: any) => (handleCardClick(card, index)),
              ref_for: true,
              ref: el => { if (isAutoPlaying.value && currentPlayingIndex.value === index) scrollToCard(el) }
            }, [
              (card.coverUrl)
                ? (_openBlock(), _createElementBlock("img", {
                    key: 0,
                    src: card.coverUrl,
                    alt: "",
                    class: "word-cover"
                  }, null, 8, _hoisted_16))
                : _createCommentVNode("", true)
            ], 10, _hoisted_15))
          }), 128))
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_17, [
      _createElementVNode("button", {
        class: _normalizeClass(["dock-item", { active: !showTranslation.value }]),
        onClick: toggleTranslation
      }, [
        _createVNode(_component_font_awesome_icon, { icon: "language" }),
        _cache[3] || (_cache[3] = _createElementVNode("span", null, "关闭翻译", -1))
      ], 2),
      _createElementVNode("button", {
        class: "dock-item",
        onClick: goToFullscreen
      }, [
        _createVNode(_component_font_awesome_icon, { icon: "expand" }),
        _cache[4] || (_cache[4] = _createElementVNode("span", null, "全屏模式", -1))
      ]),
      _createElementVNode("button", {
        class: "dock-item main-btn",
        onClick: toggleAutoPlay
      }, [
        _createVNode(_component_font_awesome_icon, {
          icon: isAutoPlaying.value ? 'pause' : 'play'
        }, null, 8, ["icon"])
      ]),
      _createElementVNode("button", {
        class: "dock-item",
        onClick: openSettings
      }, [
        _createVNode(_component_font_awesome_icon, { icon: "cog" }),
        _cache[5] || (_cache[5] = _createElementVNode("span", null, "显示设置", -1))
      ]),
      _createElementVNode("button", {
        class: "dock-item",
        onClick: openTestMode
      }, [
        _createVNode(_component_font_awesome_icon, { icon: "clipboard-check" }),
        _cache[6] || (_cache[6] = _createElementVNode("span", null, "测试练习", -1))
      ])
    ]),
    _createVNode(_Transition, { name: "fullscreen" }, {
      default: _withCtx(() => [
        (fullscreenCard.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "fullscreen-overlay",
              onClick: _withModifiers(closeFullscreen, ["self"])
            }, [
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("div", {
                  class: "fullscreen-bg",
                  style: _normalizeStyle({ background: fullscreenCard.value.bgColor })
                }, null, 4),
                _createElementVNode("button", {
                  class: "close-btn",
                  onClick: closeFullscreen
                }, [
                  _createVNode(_component_font_awesome_icon, { icon: "times" })
                ]),
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("div", _hoisted_20, [
                    _createElementVNode("div", {
                      class: "fs-icon",
                      style: _normalizeStyle({ background: fullscreenCard.value.bgColor })
                    }, [
                      (fullscreenCard.value.coverUrl)
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            src: fullscreenCard.value.coverUrl,
                            alt: "",
                            class: "word-cover fs-cover"
                          }, null, 8, _hoisted_21))
                        : (_openBlock(), _createElementBlock("span", _hoisted_22, _toDisplayString(fullscreenCard.value.icon), 1))
                    ], 4)
                  ]),
                  _createElementVNode("h1", {
                    class: "fs-en",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (speakWord(fullscreenCard.value)))
                  }, _toDisplayString(fullscreenCard.value.en), 1),
                  _createElementVNode("p", {
                    class: _normalizeClass(["fs-zh", { hidden: !showTranslation.value }])
                  }, _toDisplayString(fullscreenCard.value.zh), 3),
                  _createElementVNode("p", _hoisted_23, [
                    _createVNode(_component_font_awesome_icon, {
                      icon: "hand-pointer",
                      class: "hint-icon"
                    }),
                    _cache[7] || (_cache[7] = _createTextVNode(" 点击卡片即可发音 ", -1))
                  ])
                ]),
                _createElementVNode("button", {
                  class: "nav-btn prev",
                  onClick: _withModifiers(prevCard, ["stop"])
                }, [
                  _createVNode(_component_font_awesome_icon, { icon: "chevron-left" })
                ]),
                _createElementVNode("button", {
                  class: "nav-btn next",
                  onClick: _withModifiers(nextCard, ["stop"])
                }, [
                  _createVNode(_component_font_awesome_icon, { icon: "chevron-right" })
                ]),
                _createElementVNode("div", _hoisted_24, _toDisplayString(currentIndex.value + 1) + " / " + _toDisplayString(filteredCards.value.length), 1)
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(ParentPasswordModal, {
      ref_key: "parentPasswordModalRef",
      ref: parentPasswordModalRef
    }, null, 512)
  ]))
}
}

})